import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApproversInfo } from "@/shared/components/ApproversInfo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ReasonDialog } from "@/features/invoicing/components/ReasonDialog";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  useApproveSeedBatch,
  usePendingSeedBatches,
} from "@/features/inventory/hooks/useApprovals";
import type { SeedBatchDto } from "../services/seedBatchApi";

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const CustomerSeedApprovalsPage = () => {
  const notify = useNotification();
  const pendingQuery = usePendingSeedBatches();
  const approveMutation = useApproveSeedBatch();

  const [selected, setSelected] = useState<SeedBatchDto | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);

  const handleApprove = (sb: SeedBatchDto) => {
    approveMutation.mutate(
      { id: sb.id, request: { isApproved: true } },
      {
        onSuccess: () =>
          notify.success(
            `Seed batch ${sb.batchNumber} approved — available for sowing.`
          ),
        onError: () => notify.error("Failed to approve seed batch."),
      }
    );
  };

  const openReject = (sb: SeedBatchDto) => {
    setSelected(sb);
    setRejectOpen(true);
  };

  const handleReject = (reason: string) => {
    if (!selected) return;
    approveMutation.mutate(
      { id: selected.id, request: { isApproved: false, rejectionReason: reason } },
      {
        onSuccess: () => {
          notify.success(`Seed batch ${selected.batchNumber} rejected.`);
          setRejectOpen(false);
          setSelected(null);
        },
        onError: () => notify.error("Failed to reject seed batch."),
      }
    );
  };

  const rows = pendingQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">
          Customer Seed Approvals
        </h1>
        <p className="mt-1 text-muted-foreground">
          Review customer-supplied seed receipts awaiting approval. Approved
          batches become selectable for sowing instructions; rejected rows freeze
          for audit and the receiver records a corrected batch.
        </p>
        <div className="mt-2">
          <ApproversInfo permission="seed_batches.approve_quality" />
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Pending Seed Batches</CardTitle>
            {rows.length > 0 && (
              <Badge className="border-orange-400 bg-orange-100 text-orange-700">
                {rows.length}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pendingQuery.isLoading && (
            <p className="py-8 text-center text-muted-foreground">Loading...</p>
          )}
          {!pendingQuery.isLoading && rows.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              No seed batches awaiting approval.
            </p>
          )}
          {rows.length > 0 && (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Batch #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Crop</TableHead>
                    <TableHead>Variety</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((sb) => (
                    <TableRow key={sb.id}>
                      <TableCell className="font-medium">{sb.batchNumber}</TableCell>
                      <TableCell className="text-sm">{sb.customerName}</TableCell>
                      <TableCell className="text-sm">{sb.cropTypeName}</TableCell>
                      <TableCell className="text-sm">{sb.variety || "—"}</TableCell>
                      <TableCell className="font-semibold">
                        {sb.quantityReceived} {sb.unit}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(sb.receivedDate)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(sb)}
                            disabled={approveMutation.isPending}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => openReject(sb)}
                            disabled={approveMutation.isPending}
                          >
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject Customer Seed Batch"
        description="The Sales user who recorded this batch will be notified with this reason."
        reasonLabel="Rejection Reason"
        reasonPlaceholder="e.g. Seeds visibly mouldy, refuse to plant"
        submitLabel="Reject"
        isSubmitting={approveMutation.isPending}
        onSubmit={handleReject}
      />
    </div>
  );
};
